/**
 * My Brand Partners Page (Creator View)
 * Allows creators to accept/reject partnership invitations and view their partnerships
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import { useAuth } from '../../components/AuthProvider';

interface Partnership {
  id: string;
  brand_id: string;
  creator_id: string;
  status: 'pending' | 'active' | 'inactive' | 'rejected';
  invited_at: string;
  accepted_at: string | null;
  rejected_at: string | null;
  notes: string | null;
  brand: {
    store_name: string;
  };
  product_count: number;
}

interface ProductSetting {
  product_id: string;
  commission_rate: number;
  commission_type: 'percentage' | 'fixed';
  start_date: string | null;
  end_date: string | null;
  max_uses: number | null;
  current_uses: number;
  product: {
    product_name: string;
    price: number;
    main_images: string[];
  };
}

export default function MyBrandPartners() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const [loading, setLoading] = useState(true);
  const [creatorId, setCreatorId] = useState<string | null>(null);
  const [partnerships, setPartnerships] = useState<Partnership[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedPartnership, setSelectedPartnership] = useState<Partnership | null>(null);
  const [productSettings, setProductSettings] = useState<ProductSetting[]>([]);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);

  useEffect(() => {
    loadCreatorProfile();
  }, [user]);

  useEffect(() => {
    if (creatorId) {
      loadPartnerships();
    }
  }, [creatorId]);

  const loadCreatorProfile = async () => {
    if (!user) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    try {

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      const { data: sellerProfile, error: sellerError } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', profile.id)
        .single();

      if (sellerError) throw sellerError;

      setCreatorId(sellerProfile.id);
    } catch (err: any) {
      setError('크리에이터 프로필을 불러올 수 없습니다: ' + err.message);
    }
  };

  const loadPartnerships = async () => {
    try {
      setLoading(true);

      // First, get partnerships
      const { data: partnershipsData, error: partnershipsError } = await supabase
        .from('affiliate_partnerships')
        .select('*')
        .eq('creator_id', creatorId)
        .order('invited_at', { ascending: false });

      if (partnershipsError) throw partnershipsError;

      if (!partnershipsData || partnershipsData.length === 0) {
        setPartnerships([]);
        return;
      }

      // Get unique brand IDs
      const brandIds = Array.from(new Set(partnershipsData.map((p) => p.brand_id)));

      // Fetch brand info from seller_profile_public view
      const { data: brandsData, error: brandsError } = await supabase
        .from('seller_profile_public')
        .select('id, store_name')
        .in('id', brandIds);

      if (brandsError) {
        console.error('Error fetching brands:', brandsError);
        // Continue with null brands rather than failing completely
      }

      // Map brands by ID for quick lookup
      const brandsMap = new Map(
        (brandsData || []).map((b) => [b.id, { store_name: b.store_name }])
      );

      // Get partnership IDs to fetch product counts
      const partnershipIds = partnershipsData.map((p) => p.id);

      // Fetch product counts for each partnership
      const { data: productSettings, error: productSettingsError } = await supabase
        .from('product_affiliate_settings')
        .select('partnership_id')
        .in('partnership_id', partnershipIds);

      if (productSettingsError) {
        console.error('Error fetching product settings:', productSettingsError);
      }

      // Count products per partnership
      const productCountMap = new Map<string, number>();
      (productSettings || []).forEach((setting) => {
        if (setting.partnership_id) {
          productCountMap.set(
            setting.partnership_id,
            (productCountMap.get(setting.partnership_id) || 0) + 1
          );
        }
      });

      // Combine partnerships with brand info and product count
      const combinedData = partnershipsData.map((partnership) => ({
        ...partnership,
        brand: brandsMap.get(partnership.brand_id) || { store_name: '알 수 없음' },
        product_count: productCountMap.get(partnership.id) || 0,
      }));

      setPartnerships(combinedData);
    } catch (err: any) {
      setError('파트너십 목록을 불러올 수 없습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadProductSettings = async (partnershipId: string) => {
    try {
      const { data, error } = await supabase
        .from('product_affiliate_settings')
        .select(
          `
          product_id,
          commission_rate,
          commission_type,
          start_date,
          end_date,
          max_uses,
          current_uses,
          product:products(product_name, price, main_images)
        `
        )
        .eq('partnership_id', partnershipId)
        .eq('status', 'approved');

      if (error) throw error;

      // Transform the data to match the expected type
      const transformedData = (data || []).map((item: any) => ({
        product_id: item.product_id,
        commission_rate: item.commission_rate,
        commission_type: item.commission_type,
        start_date: item.start_date,
        end_date: item.end_date,
        max_uses: item.max_uses,
        current_uses: item.current_uses ?? 0,
        product: Array.isArray(item.product) ? item.product[0] : item.product,
      }));

      setProductSettings(transformedData);
    } catch (err: any) {
      setError('제품 설정을 불러올 수 없습니다: ' + err.message);
    }
  };

  const handleAcceptPartnership = async (partnershipId: string) => {
    try {
      const { error } = await supabase
        .from('affiliate_partnerships')
        .update({
          status: 'active',
          accepted_at: new Date().toISOString(),
        })
        .eq('id', partnershipId);

      if (error) throw error;

      setSuccess('파트너십을 수락했습니다!');
      loadPartnerships();
    } catch (err: any) {
      setError('수락 실패: ' + err.message);
    }
  };

  const handleRejectPartnership = async (partnershipId: string) => {
    if (!window.confirm('정말로 이 파트너십을 거절하시겠습니까?')) return;

    try {
      const { error } = await supabase
        .from('affiliate_partnerships')
        .update({
          status: 'rejected',
          rejected_at: new Date().toISOString(),
        })
        .eq('id', partnershipId);

      if (error) throw error;

      setSuccess('파트너십을 거절했습니다');
      loadPartnerships();
    } catch (err: any) {
      setError('거절 실패: ' + err.message);
    }
  };

  const handleViewDetails = async (partnership: Partnership) => {
    setSelectedPartnership(partnership);
    await loadProductSettings(partnership.id);
    setOpenDetailsDialog(true);
  };

  const handleProductClick = (productId: string) => {
    const flutterWebUrl = `https://clozet.my/#/product/${productId}`;
    window.open(flutterWebUrl, '_blank', 'noopener,noreferrer');
  };

  const getStatusChip = (status: string) => {
    const statusConfig = {
      pending: { label: '대기중', color: 'warning' as const },
      active: { label: '활성', color: 'success' as const },
      inactive: { label: '비활성', color: 'default' as const },
      rejected: { label: '거절됨', color: 'error' as const },
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const filteredPartnerships = partnerships.filter((p) => {
    if (tabValue === 0) return p.status === 'active';
    if (tabValue === 1) return p.status === 'pending';
    if (tabValue === 2) return p.status === 'inactive' || p.status === 'rejected';
    return true;
  });

  const pendingCount = partnerships.filter((p) => p.status === 'pending').length;

  return (
    <Box sx={styles.pageContainer}>
      <Typography variant="h4" fontWeight="bold" sx={{ ...styles.title, mb: 3 }}>
        브랜드 파트너십
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Card sx={{ ...styles.paper, mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={styles.text}>{partnerships.filter((p) => p.status === 'active').length}</Typography>
              <Typography variant="body2" color="text.secondary" sx={styles.text}>
                활성 파트너십
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" color="warning.main" sx={styles.text}>
                {pendingCount}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={styles.text}>
                대기 중인 초대
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={styles.text}>{partnerships.length}</Typography>
              <Typography variant="body2" color="text.secondary" sx={styles.text}>
                총 파트너십
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper sx={{ ...styles.paper, mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label={`활성 (${partnerships.filter((p) => p.status === 'active').length})`} />
          <Tab label={`대기중 (${pendingCount})`} />
          <Tab
            label={`비활성/거절 (${partnerships.filter((p) => p.status === 'inactive' || p.status === 'rejected').length})`}
          />
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ ...styles.paper, ...styles.tableContainer }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>브랜드</TableCell>
                <TableCell>메모</TableCell>
                <TableCell>상태</TableCell>
                <TableCell>초대일</TableCell>
                <TableCell>상품 수</TableCell>
                <TableCell align="right">액션</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPartnerships.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary" sx={styles.text}>파트너십이 없습니다</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPartnerships.map((partnership) => (
                  <TableRow key={partnership.id}>
                    <TableCell>
                      <Typography fontWeight="bold" sx={styles.text}>{partnership.brand.store_name}</Typography>
                    </TableCell>
                    <TableCell>
                      {partnership.notes ? (
                        <Typography variant="body2" color="text.secondary" sx={styles.text}>
                          {partnership.notes}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled" sx={styles.text}>
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{getStatusChip(partnership.status)}</TableCell>
                    <TableCell>{new Date(partnership.invited_at).toLocaleDateString()}</TableCell>
                    <TableCell>{partnership.product_count}</TableCell>
                    <TableCell align="right">
                      {partnership.status === 'pending' && (
                        <>
                          <Button
                            size="small"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => handleAcceptPartnership(partnership.id)}
                            sx={{ mr: 1 }}
                          >
                            수락
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<CancelIcon />}
                            onClick={() => handleRejectPartnership(partnership.id)}
                          >
                            거절
                          </Button>
                        </>
                      )}
                      {partnership.status === 'active' && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewDetails(partnership)}
                        >
                          상세보기
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Details Dialog */}
      <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: styles.paper }}>
        <DialogTitle sx={styles.text}>
          {selectedPartnership?.brand.store_name} - 제품 및 커미션 정보
        </DialogTitle>
        <DialogContent>
          {productSettings.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              아직 승인된 제품이 없습니다
            </Alert>
          ) : (
            <TableContainer sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>제품명</TableCell>
                    <TableCell>가격</TableCell>
                    <TableCell>커미션</TableCell>
                    <TableCell>게시 기간</TableCell>
                    <TableCell>사용 횟수</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productSettings.map((setting) => {
                    const formatDate = (dateString: string | null) => {
                      if (!dateString) return '-';
                      const date = new Date(dateString);
                      return date.toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      });
                    };

                    return (
                      <TableRow key={setting.product_id}>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.7 }
                            }}
                            onClick={() => handleProductClick(setting.product_id)}
                          >
                            {setting.product.main_images?.[0] && (
                              <Box
                                component="img"
                                src={setting.product.main_images[0]}
                                alt={setting.product.product_name}
                                sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover' }}
                              />
                            )}
                            <Typography sx={{ '&:hover': { textDecoration: 'underline' } }}>
                              {setting.product.product_name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{setting.product.price.toLocaleString()}원</TableCell>
                        <TableCell>
                          <Chip
                            label={
                              setting.commission_type === 'percentage'
                                ? `${setting.commission_rate}%`
                                : `${setting.commission_rate.toLocaleString()}원`
                            }
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {setting.start_date || setting.end_date
                              ? `${formatDate(setting.start_date)} ~ ${formatDate(setting.end_date)}`
                              : '-'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {setting.max_uses !== null && setting.max_uses !== undefined
                              ? `${setting.current_uses} / ${setting.max_uses}`
                              : '-'
                            }
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailsDialog(false)}>{t("common.close")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
